import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';
import Layout from "./layout/Layout";
import Login from "./pages/Login";
import { usePermissions } from "./api/auth/PermissionsContext";
import { useAuth } from "./api/auth/authContext";
import { useClientCode } from "./api/auth/ClientCodeContext";
import { sidebarItems, additionalRoutes } from "./pages/pageConfig";
import { lightTheme } from './theme';
import { QueryProvider } from './providers/QueryProvider';

function getLoginPath(): string {
  const storedClientCode = localStorage.getItem("clientCode");
  if (storedClientCode && storedClientCode !== "admin") {
    return `/${storedClientCode}/login`;
  }
  return "/admin/login";
}

function ProtectedRoute({ element, permission }: { element: JSX.Element, permission: string }) {
  const { permissions } = usePermissions();
  const { clientCode } = useClientCode();
  
  // Admin gets access to all features
  if (clientCode === 'admin') {
    return element;
  }
  
  // Admin-only features: block for non-admin clients regardless of permissions
  const adminOnlyPermissions = new Set([
    "ACTIVITY_LOG",
    "JOB_ACTIVITY_LOG",
    "CLONE_JOB",
    "SCRIPT_TESTER",
    "PARSE_TEMPLATE",
    "JOB_DETAIL_EDIT", // Client users can view job list only; cannot open job-detail-edit
  ]);
  if (adminOnlyPermissions.has(permission)) {
    // Client users trying to open job-detail-edit: send back to job list
    if (permission === "JOB_DETAIL_EDIT") {
      return <Navigate to="/job-details" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }
  
  // Temporarily bypass permission check for CLONE_JOB
  if (permission === "CLONE_JOB" || permissions.includes(permission)) {
    return element;
  }
  
  // Avoid showing a "Forbidden" screen for normal user flows.
  return <Navigate to="/dashboard" replace />;
}

export default function App() {
  const { setPermissions } = usePermissions();
  const { isAuthenticated } = useAuth();
  // Force light mode for SAP UI consistency
  const [mode, setMode] = useState<'light' | 'dark'>('light');

  const theme = lightTheme;

  useEffect(() => {
    // On mount, load permissions from localStorage if present
    const stored = localStorage.getItem("permissions");
    if (stored) {
      try {
        setPermissions(JSON.parse(stored));
      } catch {}
    }
  }, []);

  return (
    <QueryProvider>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <BrowserRouter>
          <Routes>
            {/* Legacy route support */}
            <Route path="/login" element={<Navigate to="/admin/login" replace />} />

            {/* New login URLs */}
            <Route path="/admin/login" element={<Login />} />
            <Route path="/:clientCode/login" element={<Login />} />
            <Route
              element={
                isAuthenticated ? (
                  <Layout setMode={setMode} mode={mode} />
                ) : (
                  <Navigate to={getLoginPath()} replace />
                )
              }
            >
              {sidebarItems.map(item => (
                <Route
                  key={item.key}
                  path={item.path}
                  element={<ProtectedRoute element={item.element} permission={item.permission} />}
                />
              ))}
              {additionalRoutes.map(item => (
                <Route
                  key={item.key}
                  path={item.path}
                  element={<ProtectedRoute element={item.element} permission={item.permission} />}
                />
              ))}
            </Route>
            <Route path="*" element={<Navigate to={isAuthenticated ? "/dashboard" : getLoginPath()} replace />} />
          </Routes>
        </BrowserRouter>
      </ThemeProvider>
    </QueryProvider>
  );
}
